import { DropdownButton, Text } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useMemo, useRef } from 'react';
import { useSetState } from 'react-use';

import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { useCommandHandler } from 'utils/commands';

import { TopbarButton } from '../../components/topbarButton/topbarButton';
import Phone from '../containers/phone';
import SearchUser from '../containers/searchUser';
import { DispatchProps, StateProps } from '../containers/telephonyTopbarButton';

import { OutboundNumberSelector } from './OutboundNumberSelector';
import styles from './telephonyTopbarButton.module.scss';

export type OwnProps = {
  className?: string;
  alignCenter?: boolean;
};

type PropsT = StateProps & DispatchProps & OwnProps;

type StateT = {
  fromNumberFormat: string | null;
  isOwnNumbersViewOpen: boolean;
  fromNumberCountryCode?: string | null;
};

export const TelephonyTopbarButton = (props: PropsT): JSX.Element => {
  const {
    activePhoneMetaConversation,
    alignCenter,
    canMakeOutBoundCall,
    isInternalCall,
    isTelephonyDropdownOpen,
    telephonyCloseDropdown,
    telephonyOpenDropdown,
  } = props;
  const [state, setState] = useSetState<StateT>({
    fromNumberFormat: null,
    isOwnNumbersViewOpen: false,
    fromNumberCountryCode: null,
  });

  const dropdownButtonRef = useRef<DropdownButton | null>(null);

  useCommandHandler({
    name: 'app:openDialer',
    actionFn: () => {
      dropdownButtonRef.current?.handleDropdownToggle();
    },
  });

  const toggleDropdown = useCallback(
    (isOpen) => {
      if (isOpen === true) {
        telephonyOpenDropdown();
        ampli.topbarPhonesOpened();
      } else {
        telephonyCloseDropdown();
      }
    },
    [telephonyCloseDropdown, telephonyOpenDropdown],
  );

  const setFromNumberCountryCode = useCallback(
    (countryCodeState: string | null) => {
      setState({
        fromNumberCountryCode: countryCodeState,
      });
    },
    [setState],
  );

  const returnFromNumberFormat = useCallback(
    (fromNumberFormat: string | null) => {
      setState({ fromNumberFormat });
    },
    [setState],
  );

  const toggleOwnNumbersView = useCallback(
    (isOwnNumbersViewOpen: boolean) => {
      setState({ isOwnNumbersViewOpen });
    },
    [setState],
  );

  const { phone, activeCall, showNotificationIndicator } = useMemo(() => {
    if (activePhoneMetaConversation !== null) {
      return {
        phone: (
          <li className={styles.phoneWrapper}>
            <Phone
              isInternalCall={isInternalCall}
              canMakeOutBoundCall={canMakeOutBoundCall}
              metaConversation={activePhoneMetaConversation}
            />
          </li>
        ),
        activeCall: (
          <li role="headline" className={styles.headline}>
            <Text.Heading>Phone</Text.Heading>
            <span className={styles.badge}>Active</span>
          </li>
        ),
        showNotificationIndicator: true,
      };
    }

    return {
      phone: null,
      activeCall: (
        <li role="headline" className={styles.headline}>
          <Text.Heading>Phone</Text.Heading>
        </li>
      ),
      showNotificationIndicator: false,
    };
  }, [activePhoneMetaConversation, canMakeOutBoundCall, isInternalCall]);

  const rootClass = classnames({
    [styles.root]: styles.root,
  });

  const dropdownContent = (
    <ul
      className={classnames({
        [styles.dropdownContent]: true,
        [styles.fullWidthContent]: alignCenter,
      })}
    >
      {activeCall}
      <li role="separator" className={styles.divider}></li>
      {phone}
      <OutboundNumberSelector
        setFromNumberCountryCode={setFromNumberCountryCode}
        returnFromNumberFormat={returnFromNumberFormat}
        fromNumberFormat={state.fromNumberFormat}
        isOwnNumbersViewOpen={state.isOwnNumbersViewOpen}
        toggleOwnNumbersView={toggleOwnNumbersView}
      />
      <li role="separator" className={styles.divider}></li>
      <SearchUser
        fromNumberFormat={state.fromNumberFormat}
        toggleOwnNumbersView={toggleOwnNumbersView}
        canMakeOutBoundCall={canMakeOutBoundCall}
        fromNumberCountryCode={state.fromNumberCountryCode}
        setFromNumberCountryCode={setFromNumberCountryCode}
        isOwnNumbersViewOpen={state.isOwnNumbersViewOpen}
      />
    </ul>
  );

  const tooltip = (
    <CommandKeybindingsTooltipContent commandName="app:openDialer">
      Phone dialer
    </CommandKeybindingsTooltipContent>
  );

  return (
    <DropdownButton
      ref={dropdownButtonRef}
      icon="phone"
      className={rootClass}
      onToggle={toggleDropdown}
      noArrow
      noStyle
      show={isTelephonyDropdownOpen}
      dropdownProps={{
        closeOnBlur: false,
        isOpen: isTelephonyDropdownOpen,
        className: classnames({
          [styles.dropdownMenuAlignCenter]: alignCenter,
        }),
      }}
      dropdown={dropdownContent}
    >
      <TopbarButton
        showNotificationIndicator={showNotificationIndicator}
        icon="phone"
        tooltip={tooltip}
      />
    </DropdownButton>
  );
};
